import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { apiFetch } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import SectionNav, { WORKSPACE_SECTIONS } from '@/components/workspace/SectionNav';
import SectionPlaceholder from '@/components/workspace/SectionPlaceholder';
import CvSection from '@/components/workspace/CvSection';
import CoverLetterSection from '@/components/workspace/CoverLetterSection';
import InterviewPrepSection from '@/components/workspace/InterviewPrepSection';
import PackSection from '@/components/workspace/PackSection';
// RC 1.53 Phase 4.2.1.1.d — controller-backed workspace busy state.
// The hook subscribes to controller events, so any CV variant
// lifecycle transition (dispatching → stabilizing → complete) wakes
// this component and refreshes `workspaceBusy`.
import { useOperationLifecycle } from '@/lib/operationController';
// ── Bundle U-9 — Workspace-level artefact discovery (2026-02-15) ──
import { useWorkspaceArtefactDiscovery } from '@/lib/operationController/useWorkspaceArtefactDiscovery';

/**
 * pages/ApplicationWorkspacePage.jsx — RC 1.49.7 Phase 1
 *
 * Route shell for the Application Workspace. Phase 0 shipped the
 * scaffold; Phase 1 wires the live CV section.
 *
 * Hard rules (preserved from RC 1.49.x — operator 2026-05-08):
 *   • All RC 1.49.x billing / pricing / idempotency / replay /
 *     cache-hit / post-persist debit / Interview Pack rules carry
 *     forward into the workspace via the section components — the
 *     route-based container does NOT change any backend wire
 *     contract or financial protection.
 *   • Default landing section is `cv`.
 *   • Each section MUST have three separate controls: Back,
 *     Primary, Continue. Continue is always distinct from the
 *     primary chargeable action.
 *   • CV / Letter / Fit are SKIPPABLE — Continue stays enabled.
 *   • Pack section (Phase 4) gates its Continue on prerequisites.
 *
 * Auth / ownership:
 *   • Mounted inside `<WorkspaceGuard candidate>` so unauth users
 *     are redirected to /login by the App-level guard.
 *   • Foreign job_id → ownership check via `apiFetch('/api/jobs/:jobId')`
 *     returns 403/404 → fall through to /jobs.
 */
export default function ApplicationWorkspacePage() {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  // ── URL state ───────────────────────────────────────────────────
  const requestedSection = searchParams.get('section');
  // RC 1.50 R.1 — `?section=fit` is the legacy slug from the
  // Workspace's Analyse-Fit-as-section era. The Workspace now
  // surfaces "Interview Prep" instead. Map old deep-links forward
  // so shared URLs keep working without a 404 / blank state.
  const _normalisedRequested = requestedSection === 'fit'
    ? 'interview'
    : requestedSection;
  const activeSlug = WORKSPACE_SECTIONS.includes(_normalisedRequested)
    ? _normalisedRequested
    : 'cv';

  // Normalise URL on mount if `?section=` is missing or invalid so
  // refresh / share-link / browser-back semantics are deterministic.
  useEffect(() => {
    if (requestedSection !== activeSlug) {
      const next = new URLSearchParams(searchParams);
      next.set('section', activeSlug);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSlug]);

  // ── Job ownership probe + Primary CV load ────────────────────────
  // Phase 1 follow-up: ownership probe routed through `apiFetch` so
  // session refresh / 401 retry / structured-error parsing all flow
  // through the single backend client. Primary CV loaded via
  // `/api/profile/me` (same shape the wizard reads at L1668).
  const [job, setJob] = useState(null);
  const [primaryCv, setPrimaryCv] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [bootstrapError, setBootstrapError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setBootstrapping(true);
    setBootstrapError(null);
    (async () => {
      try {
        const [jobRes, profileRes] = await Promise.allSettled([
          apiFetch(`/api/jobs/${jobId}`),
          apiFetch('/api/profile/me'),
        ]);
        if (cancelled) return;

        if (jobRes.status === 'rejected') {
          const err = jobRes.reason;
          const status = err?.status;
          if (status === 404 || status === 403) {
            navigate('/jobs', { replace: true });
            return;
          }
          setBootstrapError(err?.message || 'Failed to load job.');
          return;
        }
        setJob(jobRes.value || null);

        if (profileRes.status === 'fulfilled') {
          setPrimaryCv(profileRes.value?.primary_cv || null);
        }
        // profileRes failure is non-fatal — CV section will surface
        // the "no Source CV" affordance.
      } catch (err) {
        if (!cancelled) setBootstrapError(err?.message || 'Network error.');
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    return () => { cancelled = true; };
  }, [jobId, navigate]);

  // ── RC 1.49.7 C6/G3 — primaryCv user_id augmentation ───────────
  // Backend `/api/profile/me::primary_cv` SELECTs only
  // `id, original_filename, created_at` — `user_id` is intentionally
  // not on the wire. The four section components (and their
  // in-flight markers + idempotency keys + baselineFit / best-fit
  // hydration effects) all read `primaryCv?.user_id` for namespace
  // segmentation. Without augmentation those gates / keys collapse
  // to a literal `undefined` segment — harmless for the markers
  // (sessionStorage is per-tab) but FATAL for the hydration effect
  // gates (CvSection L246, CoverLetterSection L177, AnalyseFitSection
  // L192/L239, PackSection L134) which early-return on a falsy
  // `user_id`. The C6 Stage 1.5 guard simulation (operator 2026-02)
  // surfaced this via the High-CV Value Guard never firing because
  // the baselineFit effect never primed.
  //
  // Authoritative source: `useAuth().user.id` is the same Supabase
  // user id the backend's `decode_jwt_user_id` extracts from the
  // Bearer token on every request. Spreading it onto `primaryCv` at
  // the workspace boundary closes ALL FIVE latent defects with zero
  // changes inside the section components.
  const primaryCvForSections = useMemo(
    () => (
      primaryCv && user?.id
        ? { ...primaryCv, user_id: user.id }
        : primaryCv
    ),
    [primaryCv, user?.id],
  );

  // ── RC 1.50 Workspace UX Slice 0 — Interaction-safety flag ─────
  // Each section reports its in-flight state via `onBusyChange`. We
  // use this to drive the workspace-wide Generate-lock.
  //
  // RC 1.53 Phase 4.2.1.1.d — operator-locked (2026-05-12) refactor.
  // Previously a single `busy` boolean collapsed when a section
  // unmounted on tab-switch, and it ALSO gated navigation. The
  // operator's clarified intent is the inverse:
  //
  //   • Tab / section navigation: ALWAYS free (Continue, Back,
  //     section-nav pills, header "Back to Jobs" — every command
  //     button stays clickable mid-flight so users can browse).
  //   • Generate-class buttons: LOCKED across ALL sections while
  //     ANY operation is in-flight (CV, Cover Letter, Interview
  //     Prep, Application Pack). No re-entry, no concurrent run,
  //     no stale-balance double-click window.
  //
  // The map is keyed by action code so simultaneous reporters do
  // not clobber each other. The CV variant slot is additionally
  // backed by `controllerAnyRunning` (mount-independent —
  // survives a tab switch mid-stabilizing because the controller
  // state lives in module scope, not React state).
  const [busyByCode, setBusyByCode] = useState({});

  const setCvBusy           = useCallback(
    (b) => setBusyByCode((prev) => ({ ...prev, cv_variant: !!b })), [],
  );
  const setLetterBusy       = useCallback(
    (b) => setBusyByCode((prev) => ({ ...prev, cover_letter: !!b })), [],
  );
  const setInterviewBusy    = useCallback(
    (b) => setBusyByCode((prev) => ({ ...prev, interview_prep: !!b })), [],
  );
  const setPackBusy         = useCallback(
    (b) => setBusyByCode((prev) => ({ ...prev, application_pack: !!b })), [],
  );

  // Subscribing via the hook re-renders the workspace whenever ANY
  // controller-tracked operation transitions state, so the lock
  // tracks CV variant's full dispatching → stabilizing → complete
  // window even if the user navigates away from the CV tab.
  const { isAnyRunning: controllerAnyRunning } =
    useOperationLifecycle(jobId, 'cv_variant');

  const workspaceBusy = useMemo(
    () => controllerAnyRunning || Object.values(busyByCode).some(Boolean),
    [controllerAnyRunning, busyByCode],
  );

  // ── Bundle U-9.B — coordinated mount-time artefact discovery ────
  //
  // OPERATOR-LOCKED. Runs ONE shared hook for the workspace shell —
  // fires the three by-job lookups in parallel on (jobId, userId)
  // change, stamps the canonical URL handles atomically, and exposes
  // `lookupStatus` + `loadedFromHistory` to all four section
  // children. Closes the pre-U-9 race window where a Generate click
  // between mount and per-section lookup resolution dispatched a
  // generation the user did not consciously want.
  //
  // U-12.FE.B (2026-02-15) — extended with source-context filters.
  // The hook now reads the current `?source_cv_id` + `?source_cv_type`
  // URL params and passes them through to the backend so the by-job
  // lookups filter on COLUMN `cv_variant_id` (or `base_cv_id`) per
  // the operator's selected CV context. The `?from_pack` flag tells
  // the hook to NOT stamp URL handles (the pack already supplied
  // them explicitly — URL handles ARE the operator's intent).
  //
  // READ-ONLY. NEVER dispatches. NEVER charges. The per-section
  // U-2.* fallbacks remain as defence-in-depth (idempotent).
  const _sourceCvIdParam   = searchParams.get('source_cv_id');
  const _sourceCvTypeParam = searchParams.get('source_cv_type');
  const _fromPack          = searchParams.get('from_pack') !== null;
  const {
    lookupStatus,
    loadedFromHistory: workspaceLoadedFromHistory,
  } = useWorkspaceArtefactDiscovery({
    jobId,
    userId:       primaryCvForSections?.user_id,
    sourceCvId:   _sourceCvIdParam,
    sourceCvType: _sourceCvTypeParam,
    // U-13.HOOK (2026-02-15) — feed primary CV id so the discovery
    // hook can default `(base_cv, primaryCvId)` when the URL has
    // not yet stamped `?source_cv_*`. Prevents the pre-U-13 broad
    // job-only query during the cold mount → URL-write race window.
    primaryCvId:  primaryCvForSections?.id,
    fromPack:     _fromPack,
  });

  // ── RC 1.59 Slice 2 — Workspace Exit / Navigation Warning ──────
  // Operator-locked (2026-02-15). Two modes:
  //
  //   • MODE A (soft)  — workspace idle. Encourage workflow
  //     completion before exploring the rest of the app.
  //   • MODE B (strong) — any generation in flight (controller
  //     CV variant OR any section's `onBusyChange` busy bit).
  //     Warns that credits may still be charged and the preview
  //     binding may be lost on leave.
  //
  // Implementation note (operator-mandated 2026-02-15):
  // react-router-dom@7.5.1 ships `useBlocker` as STABLE, BUT it
  // requires a **data router** (`createBrowserRouter` +
  // `RouterProvider`). This app mounts the legacy `<BrowserRouter>`
  // (App.js L85), which yields a runtime invariant ("useBlocker
  // must be used within a data router"). Since the strict hold
  // forbids touching App.js routing, we implement an equivalent
  // guard at the document level using two surfaces:
  //
  //   1. Anchor-click capture — intercepts every `<a href>` click
  //      while the workspace is mounted (sidebar NavLinks, any
  //      `<Link>` in the page, footer/library cross-links). Filters
  //      out same-workspace and same-pathname links so query-only
  //      `?section=…` swaps and other in-workspace nav are NEVER
  //      intercepted.
  //   2. `popstate` capture — for browser Back/Forward. Re-pushes
  //      the current URL synchronously so the user stays on the
  //      workspace surface, then opens the modal. Confirming Leave
  //      calls `window.history.back()` which produces an
  //      uninstrumented pop (we've already removed the listener at
  //      that point via the pendingNav state).
  //
  // Programmatic exits initiated from within the page
  // (`handleExitToJob`, header "Back to Job" button) are routed
  // through `tryLeave(...)` so they trigger the same modal.
  //
  // `setSearchParams(...)` (Continue, goToSection, handleBack,
  // mount-time normalisation) updates only the search string, not
  // the pathname — neither click capture nor `popstate` fire for
  // it, so intra-workspace section nav is naturally preserved.
  //
  // `beforeunload` covers the surfaces neither React Router nor
  // the SPA can see: F5 / Ctrl+R / tab close / address-bar nav.
  // Attached ONLY while `workspaceBusy === true`, mirroring the
  // wizard's pattern at JobApplicationWizard.jsx:3281-3290. Native
  // prompt text is non-customisable per W3C.
  const [pendingNav, setPendingNav] = useState(null);   // { to, popstate }
  const [pendingLeaveMode, setPendingLeaveMode] = useState(null);

  const tryLeave = useCallback((to, popstate = false) => {
    setPendingNav({ to, popstate });
    // Sample workspaceBusy at the instant the user attempts to
    // leave so mid-modal busy transitions don't flicker the copy.
    setPendingLeaveMode(workspaceBusy ? 'busy' : 'idle');
  }, [workspaceBusy]);

  const handleStayInWorkspace = useCallback(() => {
    setPendingNav(null);
    setPendingLeaveMode(null);
  }, []);

  const handleConfirmLeave = useCallback(() => {
    const target = pendingNav;
    setPendingNav(null);
    setPendingLeaveMode(null);
    if (!target) return;
    if (target.popstate) {
      // Browser Back: pop once more. Our popstate listener is
      // still mounted, but we early-return on `pendingNav === null`
      // so the next pop completes naturally.
      window.history.back();
    } else if (target.to) {
      navigate(target.to);
    }
  }, [pendingNav, navigate]);

  const blockerIsActive = pendingNav !== null;

  // Anchor click capture — block all `<a href>` clicks that leave
  // the workspace pathname.
  useEffect(() => {
    const workspacePath = `/applications/workspace/${jobId}`;
    const handler = (e) => {
      if (e.defaultPrevented) return;
      if (e.button !== 0) return;                      // primary click only
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; // let new-tab pass
      const a = e.target.closest && e.target.closest('a[href]');
      if (!a) return;
      const href = a.getAttribute('href');
      if (!href) return;
      // External / mailto / tel / hash — let them through.
      if (/^(https?:|mailto:|tel:|#)/.test(href)) return;
      // Same-pathname (e.g. same workspace with different ?section=)
      // — query-only nav, never blocked.
      const [path] = href.split('?');
      if (path === workspacePath || path === window.location.pathname) return;
      // Block and route through the modal.
      e.preventDefault();
      e.stopPropagation();
      tryLeave(href);
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [jobId, tryLeave]);

  // popstate (browser Back / Forward) capture.
  useEffect(() => {
    // Push a sentinel state so the first Back press is observable.
    // If a Forward press lands the user back on the workspace, our
    // pushState restores their position.
    window.history.pushState(null, '', window.location.href);
    const handler = () => {
      // If the modal is already open we are mid-confirm — let the
      // pop complete naturally.
      if (pendingNav !== null) return;
      // Re-push so the user visually stays on the workspace.
      window.history.pushState(null, '', window.location.href);
      tryLeave(null, true);
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
    // pendingNav read inside the handler is captured at next render
    // because the handler closes over the latest state via effect
    // re-subscription.
  }, [pendingNav, tryLeave]);

  // `beforeunload` — only while a generation is active. Native
  // browser prompt; text is non-customisable per W3C, so we rely
  // on the browser's canonical "Leave site?" / "Reload site?"
  // message.
  useEffect(() => {
    if (!workspaceBusy) return undefined;
    const handler = (e) => {
      e.preventDefault();
      // Legacy spec — required for Chrome to surface the prompt.
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [workspaceBusy]);

  // ── Section handlers ────────────────────────────────────────────
  // RC 1.49.7 Slice 4 — Forward-only navigation (operator P0,
  // 2026-02). The workspace is now strictly forward-only:
  //   • Continue advances to the next section (idx + 1) and bumps
  //     `furthestIndex` so the user can later revisit completed
  //     sections as read-only summaries (Slice-3 disclosure cards).
  //   • Backward navigation to ALREADY-VISITED sections is allowed
  //     (idx ≤ furthestIndex). Pre-Slice-4 the wizard's modal
  //     allowed both directions; the new rule preserves the
  //     summary-revisit affordance without enabling forward-skip
  //     past gates Slice-5 depends on.
  //   • Forward-skip (idx > furthestIndex) is rejected.
  //   • Operator deep-links land on the section they specified —
  //     `furthestIndex` is initialised from URL `?section=` at
  //     mount, then advances monotonically. A pasted Pack-section
  //     URL therefore lets the user start at Pack without bouncing
  //     them back to CV.
  // Browser back is permitted (audit-only — no popstate
  // interception). Continue navigations use `replace: false` so
  // back is meaningful; URL-state writes elsewhere use
  // `replace: true` (matches CoverLetterSection.jsx:160 and the
  // Slice-5 triple).
  const [furthestIndex, setFurthestIndex] = useState(() => {
    const idx = WORKSPACE_SECTIONS.indexOf(activeSlug);
    return idx < 0 ? 0 : idx;
  });
  useEffect(() => {
    const idx = WORKSPACE_SECTIONS.indexOf(activeSlug);
    if (idx > furthestIndex) setFurthestIndex(idx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSlug]);

  const goToSection = (slug) => {
    // RC 1.53 Phase 4.2.1.1.d — navigation is ALWAYS free, even
    // mid-flight. The operator's clarified rule: command buttons
    // switch tabs freely; Generate-class buttons are the ones that
    // lock. Section-nav pills, Continue, Back, and Exit are all
    // navigation, so they remain clickable.
    if (!WORKSPACE_SECTIONS.includes(slug)) return;
    const idx = WORKSPACE_SECTIONS.indexOf(slug);
    // Forward-skip rejection — the user must reach a section by
    // Continue (or by direct URL, see `furthestIndex` initialiser).
    if (idx > furthestIndex) return;
    const next = new URLSearchParams(searchParams);
    next.set('section', slug);
    setSearchParams(next, { replace: false });
  };

  // RC 1.49.7 Slice 4 — Workspace exit handler (replaces the legacy
  // per-section Back button). The header-level "Back to Jobs"
  // (`workspace-back-to-job` testid) and CV-section "Exit
  // workspace" link both route here. Letter / Fit / Pack no
  // longer expose any inline Back affordance.
  //
  // RC 1.59 Slice 2 — Routed through `tryLeave(...)` so the soft /
  // strong exit warning fires on the programmatic exit surfaces
  // the click capture cannot see (these are `<button onClick>`
  // affordances, not `<a href>`).
  const handleExitToJob = () => {
    tryLeave(`/jobs/${jobId}`);
  };

  const handleContinue = () => {
    // RC 1.53 Phase 4.2.1.1.d — Continue is navigation, not action.
    const idx = WORKSPACE_SECTIONS.indexOf(activeSlug);
    if (idx < 0 || idx >= WORKSPACE_SECTIONS.length - 1) return;
    const nextIdx = idx + 1;
    if (nextIdx > furthestIndex) setFurthestIndex(nextIdx);
    const nextSlug = WORKSPACE_SECTIONS[nextIdx];
    const next = new URLSearchParams(searchParams);
    next.set('section', nextSlug);
    setSearchParams(next, { replace: false });
  };

  // ── RC 1.50 polish — Per-section Back navigation ─────────────────
  // Operator UX direction: Back buttons in Letter / Interview /
  // Pack footers route to the PREVIOUS workspace section
  // (Letter→CV, Interview→Letter, Pack→Interview). CV's "Exit
  // workspace" still routes to /jobs/:jobId (workspace exit) —
  // unchanged. RC 1.53 Phase 4.2.1.1.d — never blocked mid-flight.
  const handleBack = () => {
    const idx = WORKSPACE_SECTIONS.indexOf(activeSlug);
    if (idx <= 0) return; // CV (idx 0) uses handleExitToJob instead
    const prevSlug = WORKSPACE_SECTIONS[idx - 1];
    const next = new URLSearchParams(searchParams);
    next.set('section', prevSlug);
    setSearchParams(next, { replace: false });
  };

  // ── Render ──────────────────────────────────────────────────────
  if (bootstrapping) {
    return (
      <div
        data-testid="workspace-loading"
        className="flex h-[60vh] items-center justify-center"
      >
        <Loader2 className="h-6 w-6 animate-spin text-ndor-ink-muted" />
      </div>
    );
  }

  if (bootstrapError) {
    return (
      <div
        data-testid="workspace-error"
        className="mx-auto flex max-w-md flex-col items-center gap-3 py-16 text-center"
      >
        <p className="text-sm text-ndor-ink">{bootstrapError}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/jobs')}
          data-testid="workspace-error-back"
        >
          <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
          Back to Jobs
        </Button>
      </div>
    );
  }

  return (
    <div
      data-testid="application-workspace-page"
      className="mx-auto flex max-w-4xl flex-col gap-6 px-2 sm:px-4 py-6 min-w-0 max-w-full sm:max-w-4xl overflow-x-clip"
    >
      {/* ── RC 1.50 Workspace UX Phase 1 / Option A — sticky shell.
            Header + SectionNav are pinned to the top of the viewport
            so the workspace title + the four section pills stay
            visible while the section body scrolls. `overflow-x-clip`
            on the page container (instead of overflow-x-hidden)
            avoids creating a sticky containing block, so sticky
            descendants observe the viewport's scroll. The `-mx-4
            px-4` offsets restore the gutters that were on the page
            container so the sticky bg fills the full inner column.
            backdrop-blur + bg-background/95 give a frosted-glass
            effect over scrolled content. ──────────────────────── */}
      <div
        data-testid="workspace-sticky-header"
        className="sticky top-0 z-20 -mx-4 px-4 pt-2 pb-3 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border/60 flex flex-col gap-3"
      >
        <header className="flex flex-col gap-1.5 min-w-0">
          <button
            type="button"
            onClick={() => tryLeave(`/jobs/${jobId}`)}
            className="inline-flex w-full max-w-full items-center gap-1 text-xs text-ndor-ink-muted hover:text-ndor-ink min-w-0"
            data-testid="workspace-back-to-job"
          >
            <ArrowLeft className="h-3 w-3 shrink-0" />
            {/* RC 1.59 Slice 4 Mobile pass — long job titles must
                truncate cleanly with ellipsis on narrow viewports
                instead of hard-wrapping mid-word ("Cryptogra…").
                `min-w-0 truncate` is the standard flex-child
                truncation idiom; `block` ensures the inner span
                forms a truncatable line-box. */}
            <span className="block truncate min-w-0">
              {job?.title || 'Job detail'}
            </span>
          </button>
          <h1 className="text-xl sm:text-2xl font-semibold text-ndor-ink">
            Application workspace
          </h1>
          <p
            className="text-xs sm:text-sm text-ndor-ink-muted"
            data-testid="workspace-intro-tagline"
          >
            A live session for tailoring your CV, drafting your cover letter,
            preparing for interview, and assembling an Application Pack.
            {/* RC 1.55 P0 Mobile pass — long secondary copy hidden
                on narrow widths to give the tab strip room. The full
                description still surfaces on tablet+. */}
            <span className="hidden sm:inline">
              {' '}
              <span className="text-ndor-ink-muted/70">
                Completed documents are saved to your Documents library.
                Generate actions pause while a step is running — section
                navigation stays available.
              </span>
            </span>
          </p>
        </header>

        <SectionNav
          activeSlug={activeSlug}
          onSelect={goToSection}
          furthestIndex={furthestIndex}
          /* RC 1.53 Phase 4.2.1.1.d — section nav stays unlocked
             mid-flight; only Generate-class buttons lock. */
          disabled={false}
        />
      </div>

      <div className="flex flex-col gap-4" data-testid="workspace-sections-root">
        {/* RC 1.59 Slice 1 — Workspace section retention (operator-locked,
            2026-02-14). All four sections are mounted at all times; the
            inactive ones are hidden via the native `hidden` attribute
            (CSS `display:none`) so React keeps their fibers alive.

            WHY: Pre-Slice-1, switching sections used a conditional
            short-circuit (`{activeSlug === 'cv' && <CvSection .../>}`)
            which unmounted the previous section and discarded ~20-30
            `useState` slots per section (recoveryStatus, baselineFit,
            generatedResult, optionsOpen, …). Mid-flight generations
            terminating while the user was on another section never got
            to write their URL-bind side-effects, producing the
            "switch tab and come back" workaround.

            STRICT HOLD: section internals, polling, billing, finalize-
            charge, idempotency, controller, useArtefactRehydrate are
            UNCHANGED. This slice only changes the mount predicate from
            "active slug" to "always", and toggles visibility via
            `hidden`. Each section's own `onBusyChange` /
            `useOperationLifecycle` subscription continues unaltered.

            Note: the `hidden` HTML attribute is preferred over
            `style={{display:'none'}}` so screen-readers and AT respect
            the section as non-interactive while preserving the React
            fiber tree. Tailwind's `hidden` utility resolves to
            `display:none` too — equivalent. */}
        <div
          data-section="cv"
          data-testid="workspace-section-cv"
          data-active={activeSlug === 'cv' ? 'true' : 'false'}
          hidden={activeSlug !== 'cv'}
        >
          <CvSection
            job={job}
            jobId={jobId}
            primaryCv={primaryCvForSections}
            onExit={handleExitToJob}
            onContinue={handleContinue}
            onBusyChange={setCvBusy}
            workspaceBusy={workspaceBusy}
            /* U-9.B — coordinated discovery props (2026-02-15) */
            lookupStatus={lookupStatus}
            loadedFromHistory={workspaceLoadedFromHistory.cv}
            rootTestId="workspace-cv-section"
          />
        </div>
        <div
          data-section="letter"
          data-testid="workspace-section-letter"
          data-active={activeSlug === 'letter' ? 'true' : 'false'}
          hidden={activeSlug !== 'letter'}
        >
          <CoverLetterSection
            job={job}
            jobId={jobId}
            primaryCv={primaryCvForSections}
            onContinue={handleContinue}
            onBack={handleBack}
            onBusyChange={setLetterBusy}
            workspaceBusy={workspaceBusy}
            /* U-9.B — coordinated discovery props (2026-02-15) */
            lookupStatus={lookupStatus}
            loadedFromHistory={workspaceLoadedFromHistory.coverLetter}
            rootTestId="workspace-letter-section"
          />
        </div>
        <div
          data-section="interview"
          data-testid="workspace-section-interview"
          data-active={activeSlug === 'interview' ? 'true' : 'false'}
          hidden={activeSlug !== 'interview'}
        >
          <InterviewPrepSection
            job={job}
            jobId={jobId}
            primaryCv={primaryCvForSections}
            onContinue={handleContinue}
            onBack={handleBack}
            onBusyChange={setInterviewBusy}
            workspaceBusy={workspaceBusy}
            /* U-9.B — coordinated discovery props (2026-02-15) */
            lookupStatus={lookupStatus}
            loadedFromHistory={workspaceLoadedFromHistory.interviewPrep}
            rootTestId="workspace-fit-section"
          />
        </div>
        <div
          data-section="pack"
          data-testid="workspace-section-pack"
          data-active={activeSlug === 'pack' ? 'true' : 'false'}
          hidden={activeSlug !== 'pack'}
        >
          <PackSection
            job={job}
            jobId={jobId}
            primaryCv={primaryCvForSections}
            onContinue={handleContinue}
            onBack={handleBack}
            onBusyChange={setPackBusy}
            workspaceBusy={workspaceBusy}
            rootTestId="workspace-pack-section"
          />
        </div>
      </div>

      {/* RC 1.59 Slice 2 — Workspace exit warning modal. Single
          Dialog, copy branches on `pendingLeaveMode`. Locked at
          the moment the blocker fires so mid-modal busy
          transitions do not flip the copy under the user. */}
      <Dialog
        open={blockerIsActive}
        onOpenChange={(next) => { if (!next) handleStayInWorkspace(); }}
      >
        <DialogContent
          className="max-w-md"
          data-testid={
            pendingLeaveMode === 'busy'
              ? 'workspace-exit-warning-busy'
              : 'workspace-exit-warning-idle'
          }
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold tracking-tight">
              {pendingLeaveMode === 'busy' && (
                <AlertTriangle
                  className="h-5 w-5 text-ndor-warning shrink-0"
                  aria-hidden
                />
              )}
              <span data-testid="workspace-exit-warning-title">
                {pendingLeaveMode === 'busy'
                  ? 'Generation is still in progress'
                  : 'Leave this workflow?'}
              </span>
            </DialogTitle>
            <DialogDescription
              className="sr-only"
              data-testid="workspace-exit-warning-mode"
            >
              {pendingLeaveMode === 'busy'
                ? 'active-generation-warning'
                : 'idle-workspace-warning'}
            </DialogDescription>
          </DialogHeader>

          <div
            className="space-y-3 py-1 text-sm text-ndor-ink-secondary leading-relaxed"
            data-testid="workspace-exit-warning-body"
          >
            {pendingLeaveMode === 'busy' ? (
              <>
                <p>
                  Your document is still being generated. If you leave now,
                  credits may still be charged because generation has already
                  started, and the preview may not appear here immediately.
                </p>
                <p>
                  Completed documents should still be available later in{' '}
                  <span className="font-medium text-ndor-ink">Documents</span>.
                </p>
                <p className="text-ndor-ink">
                  We strongly recommend waiting for this step to finish before
                  leaving.
                </p>
              </>
            ) : (
              <>
                <p>
                  You are leaving this application workflow. Documents you have
                  already generated are saved in{' '}
                  <span className="font-medium text-ndor-ink">Documents</span>,
                  but this workflow session will close and you may need to
                  restart the application pack flow when you return.
                </p>
                <p>
                  We recommend completing the workflow before exploring other
                  areas.
                </p>
              </>
            )}
          </div>

          <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button
              variant={pendingLeaveMode === 'busy' ? 'outline' : 'ghost'}
              onClick={handleConfirmLeave}
              data-testid="workspace-exit-warning-leave"
              className="w-full sm:w-auto"
            >
              {pendingLeaveMode === 'busy' ? 'Leave anyway' : 'Leave workspace'}
            </Button>
            <Button
              variant="default"
              onClick={handleStayInWorkspace}
              data-testid="workspace-exit-warning-stay"
              className="w-full sm:w-auto"
              autoFocus
            >
              {pendingLeaveMode === 'busy'
                ? 'Stay until it finishes'
                : 'Stay and continue'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
